import React, { useEffect } from 'react';
import { InjectedRouter } from 'react-router';
import { withRouter } from 'app/context/Router';
import ConfirmDeviceHeader from 'auth/components/ConfirmDeviceHeader';
import ConfirmDeviceForm from 'auth/components/ConfirmDeviceForm';
import ConfirmDeviceFooter from 'auth/components/ConfirmDeviceFooter';
// @ts-ignore
import AuthActions from 'auth/AuthActions';
// @ts-ignore
import { redirector } from 'auth/components/SignInPage';
import ToastActions from 'actions/ToastActions';
import Device from 'entities/Device';
import EventTracking from 'lib/EventTracking';
import { MouseEventHandler } from 'types/typescript/eventHandlers';
import errorLogger from 'utils/errorLogger';

export type DeviceType = 'email' | 'phone';

type Props = {
  uid: string;
  deviceType: DeviceType;
  needsPassword: boolean;
  onGoBack?: MouseEventHandler;
  needsConfirmationErrorCode?: string;
  onConfirmationDone?: () => void;
  router: InjectedRouter;
};

function ConfirmDevice(props: Props) {
  const {
    uid,
    deviceType,
    needsPassword,
    onGoBack,
    needsConfirmationErrorCode,
    onConfirmationDone,
    router,
  } = props;

  useEffect(() => {
    requestConfirmationCode(uid, 'Code sent');

    EventTracking.trackKey(`log_in.confirm_${deviceType}_page.view`);
  }, [uid, deviceType]);

  const onSubmitCode = (uid: string, code: string) =>
    submitCodeAndSignIn(uid, code, router, onConfirmationDone);

  return (
    <>
      <ConfirmDeviceHeader
        uid={uid}
        deviceType={deviceType}
        needsPassword={needsPassword}
        onGoBack={onGoBack}
        needsConfirmationErrorCode={needsConfirmationErrorCode}
      />
      <ConfirmDeviceForm uid={uid} deviceType={deviceType} onSubmitCode={onSubmitCode} />
      <ConfirmDeviceFooter
        deviceType={deviceType}
        onResendCode={() => requestConfirmationCode(props.uid, 'Code successfully resent')}
      />
    </>
  );
}

export function getReadableDeviceType(device: DeviceType) {
  if (device === 'email') {
    return 'email address';
  } else {
    return device;
  }
}

export function requestConfirmationCode(uid: string, successMessage: string): Promise<void> {
  return Device.outboundVerify(uid).then(
    () => ToastActions.showSuccessToast(successMessage),
    () => ToastActions.showErrorToast('Error sending the code')
  );
}

export function submitCodeAndSignIn(
  uid: string,
  code: string,
  router: InjectedRouter,
  onConfirmationDone?: () => void
): Promise<void> {
  const onSuccess = ({ token }: { token: string }): Promise<void> =>
    AuthActions.accessTokenSignIn(token)
      .then(() => {
        const { attemptedTransition, onSignInRedirectTo } = redirector;
        if (attemptedTransition) {
          redirector.reset();
          router.push(attemptedTransition);
          return;
        }

        if (onSignInRedirectTo) {
          redirector.reset();
          router.push(onSignInRedirectTo);
          return;
        }

        if (onConfirmationDone) {
          onConfirmationDone();
          return;
        }

        router.push('/');
      })
      .catch(errorLogger('ConfirmDevice sign in failed'));

  return Device.createOutboundDevice(uid, code).then(onSuccess);
}

export default withRouter(ConfirmDevice);
